// ProductsPage.tsx — v2
import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Activity, Cpu, Check, ArrowRight, Zap, Brain, BarChart2, Shield,
  ChevronRight, Lock, Globe, Database, Layers,
} from 'lucide-react'

const PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || 'https://stocksignalspro.example.com'

interface Props { dark: boolean; apiBase: string; productUrl: string }

interface SSFeature { icon: React.ReactNode; title: string; desc: string }
interface DAFeature  { icon: React.ReactNode; label: string }

const ProductsPage: React.FC<Props> = ({ dark }) => {

  const ssFeatures: SSFeature[] = [
    { icon: <Zap  className="w-5 h-5" />,     title: 'Real-Time Signals',   desc: 'Live market signals powered by advanced technical analysis and AI pattern recognition. Sub-50ms latency.' },
    { icon: <Brain className="w-5 h-5" />,    title: 'Agentic Analytics',   desc: 'AI agents continuously monitor the market, surfacing opportunities and risks before they pass.' },
    { icon: <BarChart2 className="w-5 h-5" />, title: '20+ Indicators',     desc: 'EMA, VWAP, RSI, Bollinger Bands, MACD, Stochastic, Ichimoku and more for comprehensive analysis.' },
    { icon: <Shield className="w-5 h-5" />,   title: 'Risk Management',     desc: 'Smart risk calculators, position sizing tools, and protective alerts to keep your portfolio safe.' },
    { icon: <Globe className="w-5 h-5" />,    title: 'Multi-Asset',         desc: 'Equities, ETFs, crypto, forex, and commodities all in one unified dashboard.' },
    { icon: <Activity className="w-5 h-5" />, title: 'Custom Alerts',       desc: 'Trigger notifications via email, SMS, or webhook on any signal, price level, or custom condition.' },
  ]

  const daFeatures: DAFeature[] = [
    { icon: <Cpu      className="w-4 h-4" />, label: 'Autonomous Agents' },
    { icon: <Database className="w-4 h-4" />, label: 'Pipeline Orchestration' },
    { icon: <Layers   className="w-4 h-4" />, label: 'No-Code Setup' },
    { icon: <Lock     className="w-4 h-4" />, label: 'Enterprise Ready' },
    { icon: <Globe    className="w-4 h-4" />, label: '100+ Integrations' },
    { icon: <Brain    className="w-4 h-4" />, label: 'LLM-Powered' },
  ]

  const altBg   = dark ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.04)'
  const border  = 'var(--c-border)'

  return (
    <>
      <Helmet>
        <title>Products — Larxnet</title>
        <meta
          name="description"
          content="Explore Larxnet's suite of enterprise analytics products including StockSignals Pro for real-time market intelligence and DataAgents for autonomous data pipelines."
        />
        <link rel="canonical" href="https://larxnet.com/products" />
      </Helmet>

      <div className="min-h-screen page-enter" style={{ background: 'var(--c-bg)' }}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <div className="relative overflow-hidden py-24 px-6 text-center">
          <div className="absolute inset-0 bg-dots pointer-events-none" />
          <div
            className="absolute pointer-events-none"
            style={{
              inset: 0,
              background: 'radial-gradient(ellipse at 50% 0%, rgba(8,145,178,0.14) 0%, transparent 65%)',
            }}
          />
          <div className="relative max-w-3xl mx-auto">
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-7"
              style={{
                fontFamily: 'var(--font-mono)',
                background: 'rgba(34,211,238,0.07)',
                border: '1px solid rgba(34,211,238,0.20)',
                color: 'var(--c-cyan)',
              }}
            >
              Our Products
            </div>
            <h1
              className="font-display leading-none mb-5"
              style={{ fontSize: 'clamp(3rem, 9vw, 6.5rem)', color: 'var(--c-text)' }}
            >
              BUILT FOR<br />
              <span
                style={{
                  background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                EVERY SCALE
              </span>
            </h1>
            <p className="text-xl max-w-xl mx-auto" style={{ color: 'var(--c-text-2)' }}>
              From individual traders to enterprise data teams — tools that turn raw data into decisive action.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 pb-24 space-y-10">

          {/* ── Product 1: StockSignals Pro ────────────────────────────── */}
          <div
            className="glass relative rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 0 50px rgba(34,211,238,0.06), 0 20px 40px rgba(0,0,0,0.40)' }}
          >
            {/* Cyan top line */}
            <div
              className="absolute top-0 left-0 right-0 h-px pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent 5%, var(--c-cyan) 50%, transparent 95%)' }}
            />

            {/* Live banner */}
            <div
              className="flex items-center justify-between px-8 py-3"
              style={{
                background: 'linear-gradient(90deg, rgba(8,145,178,0.12) 0%, rgba(99,102,241,0.07) 100%)',
                borderBottom: `1px solid rgba(34,211,238,0.14)`,
              }}
            >
              <div className="flex items-center gap-2.5" style={{ color: 'var(--c-cyan)' }}>
                <span className="relative flex w-2 h-2">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ background: 'var(--c-cyan)', animation: 'driftOrb 1.5s ease-in-out infinite' }}
                  />
                  <span className="relative inline-flex rounded-full w-2 h-2" style={{ background: 'var(--c-cyan)' }} />
                </span>
                <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                  LIVE — Available Now
                </span>
              </div>
              <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}>
                Flagship Product
              </span>
            </div>

            <div className="p-10 lg:p-14">
              {/* Title row */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-10">
                <div className="flex items-center gap-5">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #0891b2, #6366f1)',
                      boxShadow: '0 4px 24px rgba(8,145,178,0.32)',
                    }}
                  >
                    <Activity className="w-8 h-8 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-1" style={{ color: 'var(--c-text)' }}>StockSignals Pro</h2>
                    <p className="text-base" style={{ color: 'var(--c-text-2)' }}>
                      Real-time market intelligence for traders & analysts
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <a
                    href={PRODUCT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
                  >
                    Launch Platform <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link
                    to="/register"
                    className="btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
                  >
                    Sign Up Free
                  </Link>
                </div>
              </div>

              <p className="text-base leading-relaxed mb-10 max-w-3xl" style={{ color: 'var(--c-text-2)' }}>
                StockSignals Pro delivers institutional-grade market intelligence directly to your screen.
                Powered by a suite of AI models and real-time data pipelines, it continuously scans markets
                and surfaces the signals that matter — so you can act with confidence, not guesswork.
              </p>

              {/* Feature grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {ssFeatures.map((f) => (
                  <div
                    key={f.title}
                    className="p-5 rounded-xl transition-all duration-300 cursor-default"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid var(--c-border)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(34,211,238,0.20)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--c-border)')}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ background: 'rgba(34,211,238,0.08)', color: 'var(--c-cyan)' }}
                    >
                      {f.icon}
                    </div>
                    <h4 className="font-semibold text-sm mb-1.5" style={{ color: 'var(--c-text)' }}>{f.title}</h4>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--c-text-2)' }}>{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div
                className="grid grid-cols-3 gap-4 p-6 rounded-xl"
                style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.11)' }}
              >
                {[
                  { v: '<50ms', l: 'Signal Latency' },
                  { v: '99.9%', l: 'Uptime SLA' },
                  { v: '1M+',   l: 'Daily Signals' },
                ].map((s) => (
                  <div key={s.l} className="text-center">
                    <div
                      className="font-display mb-0.5"
                      style={{
                        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                        background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {s.v}
                    </div>
                    <div className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Product 2: DataAgents ─────────────────────────────────── */}
          <div
            className="glass relative rounded-2xl overflow-hidden"
            style={{ opacity: 0.80 }}
          >
            {/* Amber top line */}
            <div
              className="absolute top-0 left-0 right-0 h-px pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent 5%, var(--c-amber) 50%, transparent 95%)' }}
            />

            {/* In-dev banner */}
            <div
              className="flex items-center justify-between px-8 py-3"
              style={{
                background: 'rgba(245,158,11,0.05)',
                borderBottom: `1px solid rgba(245,158,11,0.12)`,
              }}
            >
              <span
                className="text-sm font-semibold"
                style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}
              >
                IN DEVELOPMENT
              </span>
              <span
                className="badge-dev px-3 py-1 rounded-full text-xs font-semibold"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                Coming Soon
              </span>
            </div>

            <div className="p-10 lg:p-14">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-8">
                <div className="flex items-center gap-5">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.20)',
                    }}
                  >
                    <Cpu className="w-8 h-8" style={{ color: 'var(--c-amber)' }} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-1" style={{ color: 'var(--c-text)' }}>DataAgents</h2>
                    <p className="text-base" style={{ color: 'var(--c-text-3)' }}>
                      Autonomous enterprise data pipeline agents
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="btn-ghost flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
                >
                  Express Interest <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <p className="text-base leading-relaxed mb-8 max-w-3xl" style={{ color: 'var(--c-text-2)' }}>
                DataAgents will bring fully autonomous AI-powered agents to enterprise data pipelines.
                Connect your data sources, describe what you need, and let agents handle ingestion,
                transformation, analysis, and delivery — no coding required.
              </p>

              <div className="flex flex-wrap gap-3">
                {daFeatures.map((f) => (
                  <div
                    key={f.label}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid var(--c-border)',
                      color: 'var(--c-text-3)',
                    }}
                  >
                    <span style={{ color: 'var(--c-amber)', opacity: 0.65 }}>{f.icon}</span>
                    {f.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* More coming soon */}
          <div
            className="text-center py-16 rounded-2xl"
            style={{ border: `1px dashed ${border}` }}
          >
            <div
              className="font-display mb-2"
              style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: 'var(--c-text-3)', letterSpacing: '0.1em' }}
            >
              MORE COMING SOON
            </div>
            <p className="text-sm mb-6" style={{ color: 'var(--c-text-3)' }}>
              We're building the future of enterprise data intelligence.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
              style={{ color: 'var(--c-cyan)' }}
            >
              Join the waitlist <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
      </div>
    </>
  )
}

export default ProductsPage
